// frontend/src/api/leagues.ts
import { API_BASE_URL } from '@/config/apiConfig';
import { ApiError } from './auth';
import { createApiClient, isAxiosError } from './client';
import { LeagueRead, ApiTeamRead, LMTeamRead, ApiLeagueJoinPayload, ApiLeagueJoinViaInvitePayload, ApiLeagueCreatePayload, SetInitialWaiverOrderPayload } from './schemas';
import * as schemas from './schemas';
import type { LiveMatchupPayload } from '@/utils/liveMatchup';

export type { ApiLeagueCreatePayload } from './schemas';

const leaguesApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// createLeague function (remains the same)
export const createLeague = async (
  payload: ApiLeagueCreatePayload,
  token: string,
  idempotencyKey?: string,
): Promise<LeagueRead> => {
  if (!token) {
    throw new ApiError("Authentication required to create a league.", 401);
  }
  const url = `${API_BASE_URL}/leagues/`;
  console.log(`[API] Attempting to create league: ${payload.name} for comp ID: ${payload.competition_id}`);
  try {
    const response = await leaguesApiClient.post<LeagueRead>(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      },
    });
    console.log(`[API] League created successfully: ID ${response.data.id}, Name: ${response.data.name}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to create league.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      console.error(`[API] Axios error creating league:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      const backendDetail = error.response?.data?.detail;
      // Friendly copy for the missing-competition case BEFORE the generic
      // detail passthrough (the raw CRUD message leaks an internal DB id).
      // This case is a 400 since the endpoint's duplicate pre-resolution was
      // removed (2026-07-07); the old 404 branch here was dead code.
      if (typeof backendDetail === 'string' && /competition \(ID: \d+\) not found/i.test(backendDetail)) {
        errorMessage = "Selected competition not found or not active for fantasy seasons.";
      } else if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 400) {
        errorMessage = "Invalid league data provided. Please check inputs.";
      } else if (status === 401 || status === 403) {
        errorMessage = 'Authentication/Authorization failed.';
      }
    } else {
      console.error(`[API] Unexpected error creating league:`, error);
    }
    const userMessage = status === 400 || status === 409 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// joinLeague function (remains the same)
export const joinLeague = async (
  leagueId: number,
  payload: ApiLeagueJoinPayload,
  token: string
): Promise<ApiTeamRead> => {
  if (!token) {
    throw new ApiError("Authentication required to join a league.", 401);
  }
  if (!leagueId) {
    throw new ApiError("League ID is required to join.", 400);
  }
  const url = `${API_BASE_URL}/leagues/${leagueId}/join`;
  console.log(`[API] Attempting to join league: ${leagueId}`);
  try {
    const response = await leaguesApiClient.post<ApiTeamRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully joined league ${leagueId}, created team ID ${response.data.id}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to join league.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      console.error(`[API] Axios error joining league ${leagueId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 400) {
        errorMessage = "Invalid data provided (e.g., league ID format).";
      } else if (status === 403) {
        errorMessage = "Cannot join this league (e.g., full, or draft started).";
      } else if (status === 404) {
        errorMessage = "League not found.";
      } else if (status === 409) {
        errorMessage = "You are already a member of this league.";
      }
    } else {
      console.error(`[API] Unexpected error joining league ${leagueId}:`, error);
    }
    const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// fetchLeagueDetails function (remains the same)
export const fetchLeagueDetails = async (leagueId: number, token: string): Promise<LeagueRead> => {
    if (!token) throw new ApiError("Auth required", 401);
    const url = `${API_BASE_URL}/leagues/${leagueId}`;
    try {
        const response = await leaguesApiClient.get<LeagueRead>(url, { headers: { Authorization: `Bearer ${token}` } });
        
        // ADD THIS DEBUG LOGGING
        console.log('[DEBUG] Raw API response from fetchLeagueDetails:', JSON.stringify(response.data, null, 2));
        console.log('[DEBUG] Fantasy Season Definition:', response.data.fantasy_season_definition);
        console.log('[DEBUG] Competition from FSD:', response.data.fantasy_season_definition?.competition);
        console.log('[DEBUG] Competition ID:', response.data.fantasy_season_definition?.competition_id);
        
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch league details for ID ${leagueId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
          status = error.response?.status;
          const backendDetail = error.response?.data?.detail;
          if (typeof backendDetail === 'string') {
            errorMessage = backendDetail;
          } else if (status === 404) {
            errorMessage = "League not found.";
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        console.error(`[API] Error fetching league details for ${leagueId}:`, errorMessage, error);
        throw new ApiError(errorMessage, status);
    }
};

// fetchTeamsForRosterManagement function (remains the same)
export const fetchTeamsForRosterManagement = async (leagueId: number, token: string): Promise<LMTeamRead[]> => {
    if (!token) throw new ApiError("Auth required", 401);
    const url = `${API_BASE_URL}/leagues/${leagueId}/teams-for-roster-management`;
    try {
        const response = await leaguesApiClient.get<LMTeamRead[]>(url, { headers: { Authorization: `Bearer ${token}` } });
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch teams for LM for league ${leagueId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            errorMessage = error.response?.data?.detail || errorMessage;
        } else if (error.message) {
            errorMessage = error.message;
        }
        console.error(`[API] Error fetching teams for LM (L:${leagueId}):`, errorMessage, error);
        throw new ApiError(errorMessage, status);
    }
};

// lmAddPlayerToTeamRoster function (remains the same)
export const lmAddPlayerToTeamRoster = async (leagueId: number, teamId: number, playerId: number, token: string): Promise<schemas.RosterPlayerRead> => {
    if (!token) throw new ApiError("Auth required", 401);
    const url = `${API_BASE_URL}/leagues/${leagueId}/teams/${teamId}/roster/add-player`;
    try {
        const response = await leaguesApiClient.post<schemas.RosterPlayerRead>(url, { player_id: playerId }, { headers: { Authorization: `Bearer ${token}` } });
        return response.data;
    } catch (error: any) {
        let errorMessage = `LM failed to add player ${playerId} to team ${teamId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            errorMessage = error.response?.data?.detail || errorMessage;
        } else if (error.message) {
            errorMessage = error.message;
        }
        console.error(`[API] Error LM adding player P:${playerId} T:${teamId} L:${leagueId}:`, errorMessage, error);
        // 400/403/404/409 details are roster rules written for the commissioner
        // ("Roster is full", "Player is already rostered"); anything else is ours.
        const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
        throw new ApiError(errorMessage, status, { cause: error, userMessage });
    }
};

// lmRemovePlayerFromTeamRoster function (remains the same)
export const lmRemovePlayerFromTeamRoster = async (leagueId: number, teamId: number, playerId: number, token: string): Promise<void> => {
    if (!token) throw new ApiError("Auth required", 401);
    const url = `${API_BASE_URL}/leagues/${leagueId}/teams/${teamId}/roster/remove-player/${playerId}`;
    try {
        await leaguesApiClient.delete(url, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error: any) {
        let errorMessage = `LM failed to remove player ${playerId} from team ${teamId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            errorMessage = error.response?.data?.detail || errorMessage;
        } else if (error.message) {
            errorMessage = error.message;
        }
        console.error(`[API] Error LM removing player P:${playerId} T:${teamId} L:${leagueId}:`, errorMessage, error);
        // 400/403/404/409 details are roster rules written for the commissioner
        // ("Roster is full", "Player is already rostered"); anything else is ours.
        const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
        throw new ApiError(errorMessage, status, { cause: error, userMessage });
    }
};

// submitWaiverClaim function (remains the same)
export const submitWaiverClaim = async (
  leagueId: number,
  payload: schemas.WaiverClaimCreateApiPayload,
  token: string
): Promise<schemas.WaiverClaimRead> => {
  if (!token) {
    throw new ApiError("Authentication required to submit a waiver claim.", 401);
  }
  if (!leagueId) {
    throw new ApiError("League ID is required.", 400);
  }
  const url = `${API_BASE_URL}/leagues/${leagueId}/waiver-claims`;
  console.log(`[API] Submitting waiver claim for L:${leagueId}, AddP:${payload.player_to_add_id}, DropP:${payload.player_to_drop_id ?? 'None'}`);
  try {
    const response = await leaguesApiClient.post<schemas.WaiverClaimRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Waiver claim submitted successfully. Claim ID: ${response.data.id}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to submit waiver claim.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      console.error(`[API] Axios error submitting waiver claim for L:${leagueId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 400) {
        errorMessage = "Invalid claim data (e.g., player not available, roster full, or invalid drop).";
      } else if (status === 401 || status === 403) {
        errorMessage = 'Authentication/Authorization failed.';
      } else if (status === 404) {
        errorMessage = "League or player not found.";
      } else if (status === 409) { 
        errorMessage = "Player is already rostered, or another conflict occurred (e.g., already have a pending claim for this player).";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API] Error submitting waiver claim for L:${leagueId}:`, errorMessage, error);
    const userMessage = [400, 403, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// setInitialWaiverOrder function (remains the same)
export const setInitialWaiverOrder = async (
  leagueId: number,
  payload: SetInitialWaiverOrderPayload,
  token: string
): Promise<LeagueRead> => {
  if (!token) {
    throw new ApiError("Authentication required to set waiver order.", 401);
  }
  const url = `${API_BASE_URL}/leagues/${leagueId}/admin/set-initial-waiver-order`;
  console.log(`[API] LM setting initial waiver order for league: ${leagueId}`);
  try {
    const response = await leaguesApiClient.post<LeagueRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully set initial waiver order for league ${leagueId}.`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to set initial waiver order for league ${leagueId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      console.error(`[API] Axios error setting waiver order for L:${leagueId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 400) {
        errorMessage = "Invalid waiver order data or league status prevents this action.";
      } else if (status === 403) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (status === 404) {
        errorMessage = "League not found.";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API] Error setting waiver order for L:${leagueId}:`, errorMessage, error);
    const userMessage = status === 400 || status === 403 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// <<< --- START NEW API FUNCTION for Finalize & Start --- >>>
export const lmFinalizeRostersAndStartLeague = async (
  leagueId: number,
  token: string
): Promise<LeagueRead> => { // Backend returns the updated LeagueRead object
  if (!token) {
    throw new ApiError("Authentication required to start the league.", 401);
  }
  const url = `${API_BASE_URL}/leagues/${leagueId}/admin/finalize-rosters-and-start`;
  console.log(`[API] LM finalizing rosters and starting league: ${leagueId}`);
  try {
    // This is a POST request, but it doesn't need a body according to the backend endpoint
    const response = await leaguesApiClient.post<LeagueRead>(url, {}, { // Send empty object as body
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully finalized rosters and started league ${leagueId}. New status: ${response.data.draft_status}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to finalize rosters and start league ${leagueId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      console.error(`[API] Axios error finalizing/starting L:${leagueId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 400) {
        errorMessage = "Cannot start league. Check if all rosters are full, waiver order is set, or if league is in the correct state.";
      } else if (status === 403) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (status === 404) {
        errorMessage = "League not found.";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API] Error finalizing/starting L:${leagueId}:`, errorMessage, error);
    const userMessage = status === 400 || status === 403 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};
// <<< --- END NEW API FUNCTION --- >>>

// <<< --- ADD THIS NEW API FUNCTION AT THE END OF THE FILE --- >>>
export const fetchCurrentH2HMatchup = async (
  leagueId: number,
  token: string
): Promise<schemas.CurrentH2HMatchupResponse> => {
  if (!token) {
    throw new ApiError("Authentication required to fetch H2H matchup.", 401);
  }
  if (!leagueId || leagueId <= 0) {
    throw new ApiError("A valid League ID is required.", 400);
  }

  const url = `${API_BASE_URL}/leagues/${leagueId}/matchups/current`;
  console.log(`[API] Fetching current H2H matchup for league: ${leagueId} from ${url}`);

  try {
    const response = await leaguesApiClient.get<schemas.CurrentH2HMatchupResponse>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Received H2H matchup data for league ${leagueId}. Matchups: ${response.data.all_matchups_for_week.length}, Lineups: ${response.data.all_team_lineups_for_week.length}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch current H2H matchup for league ${leagueId}.`;
    let status: number | undefined;

    if (isAxiosError(error)) { // Ensure axios is imported at the top if not already
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 404) {
        errorMessage = "Matchup not found for this week, or league/team not found.";
      } else if (status === 403) {
        errorMessage = "You are not authorized to view this matchup.";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API] Error fetching current H2H matchup for league ${leagueId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};
// <<< --- END OF NEW API FUNCTION --- >>>

// <<< --- START: ADD THIS NEW API FUNCTION --- >>>
export const fetchLeagueWeekCalendar = async (
  leagueId: number,
  weekNumber: number,
  token: string,
): Promise<schemas.LeagueWeekCalendarNotice> => {
  if (!token) throw new ApiError('Authentication required to view the calendar.', 401);
  try {
    const response = await leaguesApiClient.get<schemas.LeagueWeekCalendarNotice>(
      `${API_BASE_URL}/leagues/${leagueId}/gameweeks/${weekNumber}/calendar`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    throw new ApiError('The calendar is temporarily unavailable.', status);
  }
};

export const fetchH2HMatchupsForWeek = async (
  leagueId: number,
  weekNumber: number, // The specific week to fetch
  token: string
): Promise<schemas.CurrentH2HMatchupResponse> => {
  if (!token) {
    throw new ApiError("Authentication required to fetch H2H matchup.", 401);
  }
  if (!leagueId || leagueId <= 0 || !weekNumber || weekNumber <= 0) {
    throw new ApiError("A valid League ID and Week Number are required.", 400);
  }

  // This URL matches the new endpoint we just created in the backend
  const url = `${API_BASE_URL}/leagues/${leagueId}/matchups/week/${weekNumber}`;
  console.log(`[API] Fetching H2H matchup data for league: ${leagueId}, week: ${weekNumber} from ${url}`);

  try {
    const response = await leaguesApiClient.get<schemas.CurrentH2HMatchupResponse>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Received H2H matchup data for L:${leagueId} W:${weekNumber}. Matchups: ${response.data.all_matchups_for_week?.length ?? 0}, Lineups: ${response.data.all_team_lineups_for_week?.length ?? 0}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch H2H matchup for league ${leagueId}, week ${weekNumber}.`;
    let status: number | undefined;

    if (isAxiosError(error)) { // Ensure axios is imported at the top
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 404) {
        errorMessage = "Matchup not found for this week, or league/team not found.";
      } else if (status === 403) {
        errorMessage = "You are not authorized to view this matchup.";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API] Error fetching H2H matchup for L:${leagueId} W:${weekNumber}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

// The matchup Live view: both lineups' scoring moments this week, the matches
// they came from and the big plays this user has watched. Clips load per
// player from the moments endpoint (fetchPlayerMatchMoments).
export const fetchMatchupLive = async (
  leagueId: number,
  matchupId: number,
  token: string,
  signal?: AbortSignal,
): Promise<LiveMatchupPayload> => {
  try {
    const response = await leaguesApiClient.get<LiveMatchupPayload>(
      `${API_BASE_URL}/leagues/${leagueId}/matchups/${matchupId}/live`,
      { headers: { Authorization: `Bearer ${token}` }, signal },
    );
    return response.data;
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    throw new ApiError(`Failed to fetch Live plays for matchup ${matchupId}.`, status, { cause: error });
  }
};

// Big plays watched to the end (idempotent), so the next visit offers Catch up.
export const recordMatchupWatchedPlays = async (
  leagueId: number,
  matchupId: number,
  playKeys: string[],
  token: string,
): Promise<void> => {
  try {
    await leaguesApiClient.post(
      `${API_BASE_URL}/leagues/${leagueId}/matchups/${matchupId}/live/watched`,
      { play_keys: playKeys },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    throw new ApiError(`Failed to record watched plays for matchup ${matchupId}.`, status, { cause: error });
  }
};

export const fetchLeagueGameweekMatches = async (
  leagueId: number,
  weekNumber: number,
  token: string,
): Promise<schemas.LeagueGameweekMatch[]> => {
  if (!token) {
    throw new ApiError('Authentication required to fetch gameweek matches.', 401);
  }
  if (!leagueId || leagueId <= 0 || !weekNumber || weekNumber <= 0) {
    throw new ApiError('A valid League ID and Week Number are required.', 400);
  }

  const url = `${API_BASE_URL}/leagues/${leagueId}/gameweeks/${weekNumber}/matches`;
  try {
    const response = await leaguesApiClient.get<schemas.LeagueGameweekMatch[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: unknown) {
    let errorMessage = `Failed to load matches for week ${weekNumber}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 403) {
        errorMessage = 'You are not authorized to view this league.';
      }
    } else if (error instanceof Error && error.message) {
      errorMessage = error.message;
    }
    throw new ApiError(errorMessage, status);
  }
};
// <<< --- END: ADD THIS NEW API FUNCTION --- >>>

// === START OF CODE TO ADD (Add this function to frontend/src/api/leagues.ts) ===

export interface ApiLeagueUpdateMaxTeamsPayload {
  max_teams: number;
}

// Function to update the maximum number of teams for a league
export const lmUpdateLeagueMaxTeams = async (
  leagueId: number,
  payload: ApiLeagueUpdateMaxTeamsPayload, // This is the request body
  token: string
): Promise<LeagueRead> => { // Backend returns the full updated LeagueRead object
  if (!token) {
    throw new ApiError("Authentication required to update league size.", 401);
  }
  if (!leagueId || leagueId <= 0) {
    throw new ApiError("Valid League ID is required.", 400);
  }
  if (!payload || typeof payload.max_teams !== 'number' || payload.max_teams <= 0) {
    throw new ApiError("Valid new maximum teams number is required in payload.", 400);
  }

  const url = `${API_BASE_URL}/leagues/${leagueId}/max-teams`; // Matches backend endpoint
  console.log(`[API] LM updating max teams for league: ${leagueId} to ${payload.max_teams}`);

  try {
    const response = await leaguesApiClient.patch<LeagueRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully updated max teams for league ${leagueId}. New max: ${response.data.max_teams}, New num_playoff_teams: ${response.data.num_playoff_teams}`);
    return response.data; // Return the updated league details
  } catch (error: any) {
    let errorMessage = `Failed to update league size for league ${leagueId}.`;
    let status: number | undefined;

    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      console.error(`[API] Axios error updating max teams for L:${leagueId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 400) {
        errorMessage = "Invalid data provided (e.g., new size invalid, or league not in correct state).";
      } else if (status === 403) {
        errorMessage = "You are not authorized to change this league's size.";
      } else if (status === 404) {
        errorMessage = "League not found.";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API] Error updating max teams for L:${leagueId}:`, errorMessage, error);
    const userMessage = status === 400 || status === 403 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

export interface ApiLeagueUpdateSettingsPayload {
  name?: string;
  password?: string;
  trade_deadline_week?: number | null;
  waiver_type?: string;
  waiver_budget?: number | null;
  waiver_process_day?: 'WED' | 'THU';
  autosub_enabled?: boolean;
  trade_review_type?: 'AUTO' | 'LEAGUE_VOTE' | 'COMMISSIONER';
  trade_review_hours?: 24 | 48 | 72;
  // 0 resets to the ceil(max_teams/3) league default. null (as read back from
  // LeagueRead) is accepted for Partial<LeagueRead> callers and means
  // "unchanged" server-side.
  trade_veto_votes_needed?: number | null;
  num_playoff_teams?: number | null;
  playoff_start_week?: number | null;
}

export const lmUpdateLeagueSettings = async (
  leagueId: number,
  payload: ApiLeagueUpdateSettingsPayload,
  token: string
): Promise<LeagueRead> => {
  if (!token) throw new ApiError("Authentication required.", 401);
  if (!leagueId) throw new ApiError("League ID is required.", 400);

  const url = `${API_BASE_URL}/leagues/${leagueId}/settings`;
  // Values may include a private league password. Log field names only.
  console.log(`[API] LM updating settings for league: ${leagueId}`, Object.keys(payload));

  try {
    const response = await leaguesApiClient.patch<LeagueRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully updated settings for league ${leagueId}.`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to update settings for league ${leagueId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = status === 400 || status === 403 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

export const lmDeleteLeague = async (
  leagueId: number,
  leagueName: string,
  token: string,
): Promise<void> => {
  if (!token) throw new ApiError('Authentication required.', 401);
  if (!leagueId) throw new ApiError('League ID is required.', 400);

  try {
    await leaguesApiClient.delete(`${API_BASE_URL}/leagues/${leagueId}`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { league_name: leagueName, confirmation: 'DELETE' },
    });
  } catch (error: unknown) {
    let errorMessage = 'The league could not be deleted.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error instanceof Error && error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// === END OF CODE TO ADD ===

// Update draft settings (schedule, timer, order)
export const updateDraftSettings = async (
  leagueId: number,
  payload: schemas.UpdateDraftSettingsPayload,
  token: string
): Promise<void> => {
  if (!token) throw new ApiError("Authentication required.", 401);
  const url = `${API_BASE_URL}/drafts/${leagueId}/settings`;
  try {
    await leaguesApiClient.patch(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: any) {
    let errorMessage = 'Failed to update draft settings.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    }
    const userMessage = status === 400 || status === 403 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// Join league via invite token (no password required)
export const joinLeagueViaInvite = async (
  leagueId: number,
  payload: ApiLeagueJoinViaInvitePayload,
  token: string
): Promise<ApiTeamRead> => {
  if (!token) throw new ApiError("Authentication required to join a league.", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/join-via-invite`;
  console.log(`[API] Joining league ${leagueId} via invite token`);
  try {
    const response = await leaguesApiClient.post<ApiTeamRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully joined league ${leagueId} via invite, team ID ${response.data.id}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to join league.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// Regenerate invite token (commissioner only)
export const regenerateInviteToken = async (
  leagueId: number,
  token: string
): Promise<LeagueRead> => {
  if (!token) throw new ApiError("Authentication required.", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/regenerate-invite-token`;
  console.log(`[API] Regenerating invite token for league ${leagueId}`);
  try {
    const response = await leaguesApiClient.post<LeagueRead>(url, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Successfully regenerated invite token for league ${leagueId}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to regenerate invite token.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    throw new ApiError(errorMessage, status, { cause: error, userMessage: status === 403 ? errorMessage : null });
  }
};
/**
 * Edit a PENDING waiver claim in place (bid amount and/or drop player).
 */
export const editWaiverClaim = async (
  leagueId: number,
  claimId: number,
  payload: schemas.WaiverClaimEditApiPayload,
  token: string
): Promise<schemas.WaiverClaimRead> => {
  if (!token) throw new ApiError("Authentication required to edit a waiver claim.", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/waiver-claims/${claimId}`;
  try {
    const response = await leaguesApiClient.put<schemas.WaiverClaimRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to edit waiver claim.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

/**
 * Reorder ALL pending claims. Priority leagues process this order directly;
 * FAAB leagues apply it within equal-dollar bids (higher bids process first).
 */
export const reorderWaiverClaims = async (
  leagueId: number,
  claimIds: number[],
  token: string
): Promise<schemas.WaiverClaimRead[]> => {
  if (!token) throw new ApiError("Authentication required to reorder waiver claims.", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/waiver-claims/reorder`;
  try {
    const response = await leaguesApiClient.post<schemas.WaiverClaimRead[]>(url, { claim_ids: claimIds }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to reorder waiver claims.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

/**
 * Cancel a PENDING waiver claim.
 */
export const cancelWaiverClaim = async (
  leagueId: number,
  claimId: number,
  token: string
): Promise<schemas.WaiverClaimRead> => {
  if (!token) throw new ApiError("Authentication required to cancel a waiver claim.", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/waiver-claims/${claimId}/cancel`;
  try {
    const response = await leaguesApiClient.post<schemas.WaiverClaimRead>(url, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to cancel waiver claim.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 404, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

export interface KickManagerResponse {
  team_id: number;
  status: string;
  claim_token: string;
  former_owner_username?: string | null;
}

// [LM Only] Remove a team's manager; the team is opened for claim and a claim
// token is returned for the copy/share link.
export const lmKickManager = async (leagueId: number, teamId: number, token: string): Promise<KickManagerResponse> => {
  if (!token) throw new ApiError("Auth required", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/teams/${teamId}/kick`;
  try {
    const response = await leaguesApiClient.post<KickManagerResponse>(url, {}, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to remove manager from team ${teamId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// [LM Only] Fetch the claim token for an already-open team so the LM can re-copy
// or re-share its claim link.
export const lmGetClaimLink = async (leagueId: number, teamId: number, token: string): Promise<KickManagerResponse> => {
  if (!token) throw new ApiError("Auth required", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/teams/${teamId}/claim-link`;
  try {
    const response = await leaguesApiClient.get<KickManagerResponse>(url, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch claim link for team ${teamId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    throw new ApiError(errorMessage, status, { cause: error });
  }
};

export interface TransferOwnershipResponse {
  league_id: number;
  new_owner_user_id: number;
  new_owner_username?: string | null;
}

// [LM Only] Hand the commissioner role to another team's manager. Atomic;
// the caller loses all LM tools the moment it succeeds.
export const lmTransferOwnership = async (leagueId: number, newOwnerTeamId: number, token: string): Promise<TransferOwnershipResponse> => {
  if (!token) throw new ApiError("Auth required", 401);
  const url = `${API_BASE_URL}/leagues/${leagueId}/transfer-ownership`;
  try {
    const response = await leaguesApiClient.post<TransferOwnershipResponse>(url, { new_owner_team_id: newOwnerTeamId }, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to transfer league management.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 409].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// --- Run It Back (SPLIT-SEASON.md 3c-LOCKED) ---
import { RunItBackPayload } from './schemas';
export const runItBack = async (
  leagueId: number, payload: RunItBackPayload, token: string
): Promise<LeagueRead> => {
  try {
    const response = await leaguesApiClient.post<LeagueRead>(
      `${API_BASE_URL}/leagues/${leagueId}/run-it-back`, payload,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    return response.data;
  } catch (error: any) {
    let msg = 'Failed to run it back.';
    if (isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
      msg = error.response.data.detail;
    }
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const userMessage = [400, 403, 409].includes(status ?? 0) ? msg : null;
    throw new ApiError(msg, status, { cause: error, userMessage });
  }
};

// --- Mock drafts (MOCK-DRAFTS.md): disposable practice leagues ---
export const createMockDraft = async (
  payload: { max_teams: number; competition_id: number; clock_seconds?: number; pick_slot?: number; additional_competition_ids?: number[] },
  token: string
): Promise<LeagueRead> => {
  if (!token) throw new ApiError('Authentication required.', 401);
  try {
    const response = await leaguesApiClient.post<LeagueRead>(
      `${API_BASE_URL}/leagues/mock-draft`, payload,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    return response.data;
  } catch (error: any) {
    let msg = 'Failed to start the mock draft.';
    if (isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
      msg = error.response.data.detail;
    }
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const userMessage = [400, 403, 409].includes(status ?? 0) ? msg : null;
    throw new ApiError(msg, status, { cause: error, userMessage });
  }
};

export const deleteMockDraft = async (leagueId: number, token: string): Promise<void> => {
  if (!token) throw new ApiError('Authentication required.', 401);
  try {
    await leaguesApiClient.delete(
      `${API_BASE_URL}/leagues/mock-draft/${leagueId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (error: any) {
    let msg = 'Failed to delete the mock draft.';
    if (isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
      msg = error.response.data.detail;
    }
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const userMessage = [400, 403, 409].includes(status ?? 0) ? msg : null;
    throw new ApiError(msg, status, { cause: error, userMessage });
  }
};
